// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { Dictionary } from "@insite/client-framework/Common/Types";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import validatePassword from "@insite/client-framework/Store/CommonHandlers/ValidatePassword";
import validateReCaptcha from "@insite/client-framework/Store/Components/ReCaptcha/Handlers/ValidateReCaptcha";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import signIn from "@insite/client-framework/Store/Context/Handlers/SignIn";
import addAccount from "@insite/client-framework/Store/Data/Accounts/Handlers/AddAccount";
import { getCreateAccountReturnUrl } from "@insite/client-framework/Store/Pages/CreateAccount/CreateAccountSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ReCaptcha from "@insite/content-library/Components/ReCaptcha";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Radio from "@insite/mobius/Radio";
import RadioGroup, { RadioGroupProps } from "@insite/mobius/RadioGroup";
import Select from "@insite/mobius/Select";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Notify from "@plumberssupply/App/Components/Notify";
import XngCreateAccountRequest from "@plumberssupply/App/Handlers/XNG/XngCreateAccountHandler";
import { createAccountConstants } from "@plumberssupply/App/Services/XngCreateAccountService";
import {
    AccountFormState,
    initialAccountFormState,
} from "@plumberssupply/App/Widgets/XngCreateAccount/XngCreateAccount.form.module";
import { xMask, xScreen } from "@plumberssupply/XDL/Utilities";
import { CreateForm } from "@plumberssupply/XDL/Utilities/form";
import { maxLengthAttr } from "@plumberssupply/XDL/Utilities/validation";
import { useValidate } from "@plumberssupply/XDL/Utilities/xValidate";
import parse from "html-react-parser";
import cloneDeep from "lodash/cloneDeep";
import React, { useContext, useEffect, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    accountDescription = "accountDescription",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.accountDescription]: string;
    };
}

const mapDispatchToProps = {
    addAccount,
    signIn,
    validatePassword,
    validateReCaptcha: makeHandlerChainAwaitable<
        { location: string; recaptchaRef: React.RefObject<ReCAPTCHA> },
        boolean
    >(validateReCaptcha),
    XngCreateAccountRequest,
};

const mapStateToProps = (state: ApplicationState) => ({
    accountSettings: getSettingsCollection(state).accountSettings,
    returnUrl: getCreateAccountReturnUrl(state),
});

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface CreateAccountStyles {
    form?: InjectableCss;
    centeringWrapper?: InjectableCss;
    errorNotify?: InjectableCss;
    mainGridContainer?: GridContainerProps;
    userGridItem?: GridItemProps;
    userGridContainer?: GridContainerProps;
    emailGridItem?: GridItemProps;
    emailTextField?: TextFieldPresentationProps;
    userNameGridItem?: GridItemProps;
    userNameTextField?: TextFieldPresentationProps;
    subscriptionGridItem?: GridItemProps;
    submitErrorGridItem?: GridItemProps;
    submitErrorTitle?: TypographyProps;
    submitGridItem?: GridItemProps;
    reCaptchaGridItem?: GridItemProps;
    submitButton?: ButtonPresentationProps;
    requirementsTitle?: TypographyProps;
    radioGroup: RadioGroupProps;
    description?: TypographyPresentationProps;
}

export const createAccountStyles: CreateAccountStyles = {
    centeringWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            flex-direction: column;
            align-items: center;
            ${xScreen("lte", "md")} {
                align-items: flex-start;
                justify-content: flex-start;
            }
        `,
    },
    userGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    userGridContainer: {
        css: css`
            border: 1px solid #ccc;
            border-radius: 2px;
            padding: 16px;
            ${xScreen("lte", "md")} {
                border: none;
                padding: 0;
            }
        `,
    },
    emailGridItem: {
        width: [12, 12, 6, 6, 6],
    },
    userNameGridItem: {
        width: [12, 12, 6, 6, 6],
    },
    submitErrorGridItem: {
        width: [12, 12, 6, 6, 6],
    },
    submitErrorTitle: {
        color: "danger",
    },
    reCaptchaGridItem: { width: 12 },
    submitGridItem: {
        width: 12,
    },
    requirementsTitle: {
        variant: "h4",
        as: "h2",
    },
    radioGroup: {
        css: css`
            display: inline-block;
            flex-direction: row;
            width: 100%;
            max-width: 650px;
            ${xScreen("lte", "md")} {
                max-width: 100%;
            }
            & > div {
                display: inline-flex;
                margin-right: 10px;
            }
        `,
    },
    form: {
        css: css`
            width: 100%;
            max-width: 650px;
            margin-top: 16px;
            ${xScreen("lte", "md")} {
                max-width: 100%;
            }
        `,
    },
    submitButton: {
        css: css`
            ${xScreen("lte", "md")} {
                width: 100%;
            }
        `,
    },
    description: {
        css: css`
            width: 100%;
            max-width: 650px;
            margin-bottom: 15px;
        `,
    },
    errorNotify: {
        css: css`
            margin-top: 15px;
        `,
    },
};

const styles = createAccountStyles;
const StyledForm = getStyledWrapper("form");

type CreateAccountFields = {
    userFirstLastName: string;
    email: string;
    phone: string;
    accountName: string;
    accountNumber: string;
    address: string;
    branch: string;
    applyForPurchasable: string;
};

const defaultValidators = {
    userFirstLastName: "required|max:80",
    email: "required|email|max:75",
    phone: "required|phone|max:14",
    accountName: "required|max:100",
    accountNumber: "required|max:20",
    address: "required|max:100",
    branch: "required",
};

const XngCreateAccount = ({ validateReCaptcha, accountSettings, XngCreateAccountRequest, fields }: Props) => {
    const [radioValue, setRadioValue] = useState<string>("Yes");
    const [errorMessage, setErrorMessage] = useState("");
    const toasterContext = useContext(ToasterContext);
    const newAccountCheck = radioValue.toLowerCase() === "yes";
    const [validators, setValidators] = useState<Dictionary<string>>(defaultValidators);
    const recaptchaRef: React.RefObject<ReCAPTCHA> = React.createRef();

    useEffect(() => {
        if (newAccountCheck) {
            setValidators(defaultValidators);
        } else {
            setValidators({
                userFirstLastName: "required|max:80",
                email: "required|email|max:75",
            });
        }
    }, [newAccountCheck]);

    const { field, handleSubmit, isSubmitting, resetForm } = useValidate({
        initialValues: {
            userFirstLastName: "",
            email: "",
            phone: "",
            accountName: "",
            accountNumber: "",
            address: "",
            branch: "",
        },
        validators,
        onSubmit: (fieldValues: CreateAccountFields, { setSubmitting }: any) => {
            submitHandler(fieldValues, setSubmitting);
        },
    });

    const accountFields = initialAccountFormState.fields;

    const businessBranches: string[] = [
        "Arlington, MA",
        "Brockton, MA",
        "Dorchester, MA",
        "Wareham, MA",
        "Fall River, MA",
        "Lebanon, NH",
        "Leominster, MA",
        "Middletown, RI",
        "Nantucket, MA",
        "New Bedford, MA",
        "Easton, MA",
        "Taunton, MA",
        "Martha's Vineyard, MA",
        "Watertown, MA",
        "Worcester, MA",
        "Newburyport, MA",
        "Woburn, MA",
    ];

    const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRadioValue(event.target.value);
    };

    const submitHandler = async (fieldValues: CreateAccountFields, setSubmitting: any) => {
        const isReCaptchaValid = await validateReCaptcha({ location: "CreateAccount", recaptchaRef });
        if (!isReCaptchaValid) {
            return;
        }

        setSubmitting(true);
        const existingAccountForm = {
            [createAccountConstants.phone]: fieldValues.phone,
            [createAccountConstants.accountName]: fieldValues.accountName,
            [createAccountConstants.accountNumber]: fieldValues.accountNumber,
            [createAccountConstants.address]: fieldValues.address,
            [createAccountConstants.branch]: fieldValues.branch,
        };

        XngCreateAccountRequest({
            [createAccountConstants.firstAndLastName]: fieldValues.userFirstLastName,
            [createAccountConstants.email]: fieldValues.email,
            [createAccountConstants.accountNewOrExisting]: radioValue,
            ...(newAccountCheck ? existingAccountForm : {}),

            onSuccess: () => {
                setSubmitting(false);
                setErrorMessage("");
                resetForm();
                toasterContext.addToast({ body: translate("Account created successfully!"), messageType: "success" });
            },
            onError: error => {
                setErrorMessage(error);
                setSubmitting(false);
            },
            onComplete(resultProps) {
                if (resultProps.apiResult?.successful) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onSuccess?.();
                } else if (resultProps.apiResult?.errorMessage) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onError?.(resultProps.apiResult.errorMessage);
                }
            },
        });
    };

    return (
        <StyledWrapper {...styles.centeringWrapper}>
            {fields.accountDescription && (
                <Typography as="p" {...styles.description}>
                    {parse(fields.accountDescription, parserOptions)}
                </Typography>
            )}
            <RadioGroup
                label={translate("Do you have a Plumber's Supply account?")}
                onChangeHandler={handleRadioChange}
                value={radioValue}
                {...styles.radioGroup}
            >
                <Radio value="Yes">{translate("Yes")}</Radio>
                <Radio value="No">{translate("No")}</Radio>
            </RadioGroup>
            <StyledForm {...styles.form} onSubmit={handleSubmit} noValidate>
                <GridContainer {...styles.mainGridContainer}>
                    <GridItem {...styles.userGridItem}>
                        <GridContainer {...styles.userGridContainer}>
                            {newAccountCheck && (
                                <>
                                    <GridItem {...styles.userNameGridItem}>
                                        <TextField
                                            {...styles.userNameTextField}
                                            {...accountFields.accountName.attrs}
                                            maxLength={maxLengthAttr.userName}
                                            {...field("accountName")}
                                        />
                                    </GridItem>
                                    <GridItem {...styles.userNameGridItem}>
                                        <TextField
                                            {...styles.userNameTextField}
                                            {...accountFields.accountNumber.attrs}
                                            maxLength={maxLengthAttr.accountNumber}
                                            {...field("accountNumber")}
                                        />
                                    </GridItem>
                                    <GridItem {...styles.userNameGridItem}>
                                        <TextField
                                            {...styles.userNameTextField}
                                            {...accountFields.Address.attrs}
                                            maxLength={maxLengthAttr.address}
                                            {...field("address")}
                                        />
                                    </GridItem>

                                    <GridItem {...styles.userNameGridItem}>
                                        <TextField
                                            {...styles.userNameTextField}
                                            {...accountFields.Phone.attrs}
                                            maxLength={maxLengthAttr.phone}
                                            {...field("phone")}
                                        />
                                    </GridItem>
                                    <GridItem {...styles.userNameGridItem}>
                                        <Select {...accountFields.branch.attrs} {...field("branch")}>
                                            <option disabled value="">
                                                {translate("Branch")}
                                            </option>
                                            {businessBranches.map((branch: string) => (
                                                <option key={branch} value={branch}>
                                                    {branch}
                                                </option>
                                            ))}
                                        </Select>
                                    </GridItem>
                                </>
                            )}

                            <GridItem {...styles.userNameGridItem}>
                                <TextField
                                    {...accountFields.userFirstLastName.attrs}
                                    maxLength={maxLengthAttr.userName}
                                    key={`userFirstLastName-${radioValue}`}
                                    {...field("userFirstLastName")}
                                />
                            </GridItem>

                            <GridItem {...styles.emailGridItem}>
                                <TextField
                                    key={`email-${radioValue}`}
                                    {...styles.emailTextField}
                                    label={`${translate("Email")} (${siteMessage(
                                        "Plumbers_This_Will_Be_Your_UserName",
                                        "This will be your user name",
                                    )})`}
                                    {...accountFields.Email.attrs}
                                    {...{ messageLabel: translate("Email") }}
                                    {...field("email")}
                                />
                            </GridItem>
                        </GridContainer>
                        <GridContainer>
                            {errorMessage && (
                                <GridItem
                                    data-test-selector="createAccount_ErrorMessage"
                                    {...styles.submitErrorGridItem}
                                >
                                    <Notify type="danger" {...styles.errorNotify}>
                                        {errorMessage}
                                    </Notify>
                                </GridItem>
                            )}
                            <GridItem {...styles.reCaptchaGridItem}>
                                <ReCaptcha recaptchaRef={recaptchaRef} location="CreateAccount" />
                            </GridItem>
                            <GridItem {...styles.submitGridItem}>
                                <Button
                                    {...styles.submitButton}
                                    type="submit"
                                    disabled={isSubmitting}
                                    data-test-selector="createAccount_createButton"
                                >
                                    {translate("Submit")}
                                </Button>
                            </GridItem>
                        </GridContainer>
                    </GridItem>
                </GridContainer>
            </StyledForm>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(XngCreateAccount),
    definition: {
        allowedContexts: ["CreateAccountPage"],
        group: "Xngage Custom" as any,
        fieldDefinitions: [
            {
                name: fields.accountDescription,
                displayName: "Create Account Description",
                editorTemplate: "RichTextField",
                defaultValue: "Create Account Description",
                fieldType: "Translatable",
                extendedConfig: { height: 170 },
            },
        ],
    },
};

export default widgetModule;
